import { useState, useEffect } from 'react';
import { getCustomers, getGasPurchases, recordGasSale } from '../../api/distributionService';
import { Customer } from '../../models/distribution';
import { useAuth } from '../../store/AuthContext';
import { useParams } from 'react-router-dom';


export default function GasSaleForm() {
  const { user } = useAuth();
  const [availablePurchases, setAvailablePurchases] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    customerId: '',
    purchaseId: '',
    quantityKg: '',
    paymentChannel: 'Cash',
    pricePerKg: localStorage.getItem('gasPricePerKg') || '0.00',
        transactionDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: '', type: '' });

  const [customers, setCustomers] = useState<Customer[]>([]);
    const { locationId } = useParams<{ locationId: string }>();


  useEffect(() => {
    // Load customers when component mounts
    const loadCustomers = async () => {
      const data = await getCustomers(locationId ?? '');
      setCustomers(data);
    };
    loadCustomers();
  }, [locationId]);

  const handleSaleSuccess = () => {
    // Refresh sales data or show toast
    console.log('New sale recorded!');
  };

  useEffect(() => {
    const loadPurchases = async () => {
      try {
        const purchases = await getGasPurchases(locationId??'');
        console.log('Available purchases:', purchases);
        setAvailablePurchases(purchases?.filter((p: any) => p.quantityKg > 0));
      } catch (error) {
        console.error('Failed to load purchases', error);
      }
    };
    loadPurchases();
  }, [locationId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.customerId) {
      newErrors.customerId = 'Select customer';
    }
    if (!formData.purchaseId) {
      newErrors.purchaseId = 'Select purchase batch';
    }
    if (!formData.quantityKg || parseFloat(formData.quantityKg) <= 0) {
      newErrors.quantityKg = 'Quantity must be greater than 0';
    }
    if (!formData.pricePerKg || parseFloat(formData.pricePerKg) <= 0) {
      newErrors.pricePerKg = 'Price must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await recordGasSale({
        locationId: locationId ?? '',
        customerId: formData.customerId || undefined,
        purchaseId: formData.purchaseId || undefined,
        quantityKg: parseFloat(formData.quantityKg),
        pricePerKg: parseFloat(formData.pricePerKg),
        paymentChannel: formData.paymentChannel,
        transactionDate: formData.transactionDate,
      });

      localStorage.setItem('gasPricePerKg', formData.pricePerKg);

      setSubmitMessage({ text: 'Sale recorded successfully!', type: 'success' });
      setFormData({
        customerId: '',
        purchaseId: '',
        quantityKg: '',
        pricePerKg: '',
        paymentChannel: 'Cash',
        transactionDate: new Date().toISOString().split('T')[0],
      });
      handleSaleSuccess();
    } catch (error) {
      setSubmitMessage({ text: 'Failed to record sale', type: 'error' });
      console.error('Sale error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
 <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Gas Sales</h1>

      
    <div className="card p-4">
      <h3 className="text-lg font-semibold mb-4">Record Gas Sale</h3>
      
      {submitMessage.text && (
        <div className={`mb-4 p-3 rounded ${submitMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {submitMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Customer (Optional)</label>
          <select
            name="customerId"
            value={formData.customerId}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Customer</option>
            {customers?.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.name} ({customer.phone})
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Purchase Batch (Optional)</label>
          <select
            name="purchaseId"
            value={formData.purchaseId}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          >
            <option value="">Select Purchase Batch</option>
            {availablePurchases?.map(purchase => (
              <option key={purchase.id} value={purchase.id}>
                {purchase.batchNumber} ({purchase.quantityKg}kg available)
              </option>
            ))}
          </select>
        </div>

   <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Price per kg *
            {errors.pricePerKg && <span className="text-red-500 text-xs ml-2">{errors.pricePerKg}</span>}
          </label>
          <input
            type="number"
            name="pricePerKg"
            min="0.01"
            step="0.01"
            value={formData.pricePerKg}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${errors.pricePerKg ? 'border-red-500' : ''}`}
            required
          />
        </div>


        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Quantity (kg) *
            {errors.quantityKg && <span className="text-red-500 text-xs ml-2">{errors.quantityKg}</span>}
          </label>
          <input
            type="number"
            name="quantityKg"
            min="0.01"
            step="0.01"
            value={formData.quantityKg}
            onChange={handleChange}
            className={`w-full p-2 border rounded ${errors.quantityKg ? 'border-red-500' : ''}`}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Payment Channel</label>
          <select
            name="paymentChannel"
            value={formData.paymentChannel || ''}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          >
            <option value="Cash">Cash</option>
            <option value="POS">POS</option>
          </select>
        </div>
     
        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Total Amount</label>
          <div className="p-2 bg-gray-100 rounded">
            {formData.quantityKg && formData.pricePerKg
              ? (parseFloat(formData.quantityKg) * parseFloat(formData.pricePerKg)).toFixed(2)
              : '0.00'}
          </div>
        </div>

      <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Date
            {errors.transactionDate && <span className="text-red-500 text-xs ml-2">{errors.transactionDate}</span>}
          </label>
          <input
            type="date"
            name="transactionDate"
            value={formData.transactionDate}
            className={`w-full p-2 border rounded ${errors.transactionDate ? 'border-red-500' : ''}`}
            required
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-2 px-4 rounded text-white ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {isSubmitting ? 'Processing...' : 'Record Sale'}
        </button>
      </form>
    </div>
      {/* <GasSaleForm 
        locationId={locationId} 
        customers={customers}
        onSuccess={handleSaleSuccess}
      /> */}
    </div>


  );
}